export const assetTypes = {
    IMAGE: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'],
    FONT: ['woff', 'woff2', 'ttf', 'otf'],
    AUDIO: ['mp3', 'wav', 'ogg'],
    VIDEO: ['mp4', 'webm']
  };
  
  class AssetPreloader {
    constructor() {
      this.loadedAssets = new Map();
      this.loading = false;
      this.progress = 0;
      this.total = 0;
    }
  
    getFileExtension(path) {
      return path.split('.').pop().toLowerCase();
    }
  
    getAssetType(extension) {
      for (const [type, extensions] of Object.entries(assetTypes)) {
        if (extensions.includes(extension)) return type;
      }
      return 'OTHER';
    }
  
    async preloadImage(path) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = path;
        img.onload = () => {
          this.loadedAssets.set(path, img);
          resolve(img);
        };
        img.onerror = reject;
      });
    }
  
    async preloadFont(path) {
      try {
        const font = new FontFace('PreloadedFont', `url(${path})`);
        const loadedFont = await font.load();
        document.fonts.add(loadedFont);
        this.loadedAssets.set(path, loadedFont);
        return loadedFont;
      } catch (error) {
        throw new Error(`Failed to load font: ${path}`);
      }
    }
  
    async preloadAudio(path) {
      return new Promise((resolve, reject) => {
        const audio = new Audio();
        audio.src = path;
        audio.oncanplaythrough = () => {
          this.loadedAssets.set(path, audio);
          resolve(audio);
        };
        audio.onerror = reject;
      });
    }
  
    async preloadVideo(path) {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.src = path;
        video.preload = 'auto';
        video.oncanplaythrough = () => {
          this.loadedAssets.set(path, video);
          resolve(video);
        };
        video.onerror = reject;
      });
    }
  
    async preloadAsset(path) {
      const extension = this.getFileExtension(path);
      const type = this.getAssetType(extension);
  
      try {
        switch (type) {
          case 'IMAGE':
            return await this.preloadImage(path);
          case 'FONT':
            return await this.preloadFont(path);
          case 'AUDIO':
            return await this.preloadAudio(path);
          case 'VIDEO':
            return await this.preloadVideo(path);
          default:
            return await fetch(path);
        }
      } catch (error) {
        console.error(`Failed to preload asset: ${path}`, error);
        throw error;
      }
    }
  
    async preloadAssets(assets, onProgress) {
      if (this.loading) return;
      
      this.loading = true;
      this.progress = 0;
      this.total = assets.length;
  
      const promises = assets.map(async (path, index) => {
        try {
          await this.preloadAsset(path);
          this.progress = ((index + 1) / this.total) * 100;
          if (onProgress) onProgress(this.progress);
        } catch (error) {
          console.error(`Error preloading ${path}:`, error);
        }
      });
  
      await Promise.all(promises);
      this.loading = false;
      return this.loadedAssets;
    }
  
    getLoadedAsset(path) {
      return this.loadedAssets.get(path);
    }
  }
  
  const assetPreloader = new AssetPreloader();
  export default assetPreloader;